import {geometry, EPSILON} from "_src/engine/constants";
import {getBboxSize} from "_src/engine/math";
import intersectEvaluator from "_src/engine/intersect_evaluator";

import {vec3, vec4} from "_src/libs/gl-matrix";

import workspace from "_src/workspace";

const _vec3Tmp = vec3.create();

export default {

  perspectiveTransFromTargetAngles(horAngle, vertAngle, target, dist, outTrans) {

    outTrans[0] = target[0] - Math.cos(vertAngle) * dist * Math.sin(horAngle);
    outTrans[1] = target[1] + Math.cos(vertAngle) * dist * Math.cos(horAngle);
    outTrans[2] = target[2] + Math.sin(vertAngle) * dist;

    return outTrans;

  },

  orthoTransFromTargetAngles : (function() {

    const vec3Tmp = vec3.create();
    const vec3Tmp2 = vec3.create();

    return function(camera, horAngle, vertAngle, target, dist, outTrans) {

      this.perspectiveTransFromTargetAngles(horAngle, vertAngle, target, dist, outTrans);

      const view = vec3.set(vec3Tmp, 0, 0, -1);
      vec3.transformQuat(view, view, camera.quat);
      const sceneMinmax = workspace.getBBox((object) => {return object.loaded});

      const perspTrans = vec3.copy(vec3Tmp2, outTrans);

      recalculateOrthoTrans(outTrans, view, sceneMinmax, camera.near);

      const distance = this.calcDistanceToTargetPlane(perspTrans, view, target);

      const height = Math.tan(0.5 * camera.fov) * distance;
      camera.top = height;
      camera.bottom = -height;

      return outTrans;

    }

  })(),

  toReference(dst) {
    if (Math.abs(dst[0]) < EPSILON) dst[0] = 0;
    if (Math.abs(dst[1]) < EPSILON) dst[1] = 0;
    if (Math.abs(dst[2]) < EPSILON) dst[2] = 0;

    if (Math.abs(dst[0]) > 1.0) dst[0] = Math.sign(dst[0]) * 1.0;
    if (Math.abs(dst[1]) > 1.0) dst[1] = Math.sign(dst[1]) * 1.0;
    if (Math.abs(dst[2]) > 1.0) dst[2] = Math.sign(dst[2]) * 1.0;

    return dst;
  },

  viewFromAngles(dest, horAngle, vertAngle) {

    dest[0] = Math.cos(vertAngle) * Math.sin(horAngle);
    dest[1] = -Math.cos(vertAngle) * Math.cos(horAngle);
    dest[2] = -Math.sin(vertAngle);

    return dest;

  },

  anglesFromView(direction) {

    const correctedDirection = vec3.copy(_vec3Tmp, direction);
    this.toReference(correctedDirection);

    let horAngle = Math.atan2(correctedDirection[0], -correctedDirection[1]);
    let vertAngle = -Math.asin(Math.min(Math.max(correctedDirection[2], -1.0), 1.0));

    return [horAngle, vertAngle];

  },

  anglesFromViewOriented(view) {

    const dotVZ = vec3.dot(view, geometry.AXIS_Z);

    if (dotVZ == -1) {
      return [0, 0.5 * Math.PI];
    } else if (dotVZ == 1) {
      return [Math.PI, -0.5 * Math.PI];
    }

    let horAngle = Math.atan2(view[0], -view[1]);
    let vertAngle = -Math.asin(Math.min(Math.max(view[2], -1.0), 1.0));

    return [horAngle, vertAngle];

  },

  checkIsUpsideDown : (function() {

    const _vec3Tmp = vec3.create();

    return function(rotation, upVec) {

      const up = vec3.set(_vec3Tmp, 0, 1, 0);
      vec3.transformQuat(up, up, rotation);

      return vec3.dot(up, upVec) < 0;

    }

  })(),

  calculateBBoxOptimalDistance(minmax, object) {
    return 0.5 * getBboxSize(minmax) / Math.tan(0.5 * object.fov);
  },

  getVisibleFieldSize() {
    const sceneMinmax = workspace.getBBox((object) => {return object.loaded});
    return 5 * getBboxSize(sceneMinmax);
  },

  calcDistanceToTargetPlane : (function() {

    const point = vec3.create();
    const vec3Tmp = vec3.create();

    return function(camPos, view, target) {

      if (intersectEvaluator.linePlaneTest(view, target, camPos, view, point)) {
        const sign = Math.sign(vec3.dot(view, vec3.sub(vec3Tmp, target, camPos)));
        return sign * vec3.distance(point, camPos);
      }

      return 0;

    }

  })(),

  getViewTargetPlaneIntersection: (function() {

    const point = vec3.create();
    const view = vec3.create();

    return function(camera, target) {

      vec3.set(view, 0, 0, -1);
      vec3.transformQuat(view, view, camera.quat);

      if (intersectEvaluator.linePlaneTest(view, target, camera.trans, view, point)) {
        vec3.copy(target, point);
      }

      return target;

    }

  })(),

}

const recalculateOrthoTrans = (function() {

  const vec3Tmp = vec3.create();
  const nearPlane = vec4.create();

  return function(trans, view, minmax, near) {

    vec3.copy(nearPlane, view);
    nearPlane[3] = -vec3.dot(trans, view);

    let distance = near;

    for (let i = 0; i < 8; ++i) {

      const x = minmax[    3 * ((i & 0b100) >> 2)];
      const y = minmax[1 + 3 * ((i & 0b010) >> 1)];
      const z = minmax[2 + 3 * ((i & 0b001))];

      const point = vec3.set(vec3Tmp, x, y, z);
      const dist = vec3.dot(point, nearPlane) + nearPlane[3];

      if (dist < 0) {
        distance = Math.max(distance, -dist + near);
      }

    }

    vec3.scaleAndAdd(trans, trans, view, -distance);

  }

})();